import dynamic from 'next/dynamic'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Auth } from 'aws-amplify'
import * as Sentry from '@sentry/nextjs'
import intlTelInput from 'intl-tel-input'

import { User, useSession } from '../providers/session'
import { Loader } from '../components/atoms/loader'
import { recordSignup } from '../utils/conversions'
import { trackAction } from '../utils/analytics'

const Layout = dynamic(() => import('../components/layout'))

const timer = (ms) => new Promise((res) => setTimeout(res, ms))

const ini = (el, setIti) => {
  try {
    // @ts-ignore
    const x = window.intlTelInputGlobals && window.intlTelInputGlobals.loadUtils('/tel.utils.min.js')
    if (x && x.then) {
      x.then(() => {
        setIti(
          intlTelInput(el, {
            initialCountry: 'gb',
          })
        )
      })
    }
  } catch (err) {
    console.log(err)
  }
}

const bootPhone = (setIti) => {
  const ele = document.getElementById('phoneNumber')
  if (ele) {
    try {
      // @ts-ignore
      if (window.intlTelInputGlobals && window.intlTelInputGlobals && window.intlTelInputGlobals.loadUtils) {
        ini(ele, setIti)
      } else {
        setTimeout(() => {
          ini(ele, setIti)
        }, 2500)
      }
    } catch (err) {
      Sentry.captureException(err)
      setTimeout(() => {
        const xe = document.getElementById('phoneNumber')
        // @ts-ignore
        if (window.intlTelInputGlobals && window.intlTelInputGlobals && window.intlTelInputGlobals.loadUtils) {
          ini(xe, setIti)
        } else {
          setTimeout(() => {
            ini(xe, setIti)
          }, 2500)
        }
      }, 2500)
    }
  } else {
    setTimeout(() => {
      const xe = document.getElementById('phoneNumber')
      // @ts-ignore
      if (window.intlTelInputGlobals && window.intlTelInputGlobals && window.intlTelInputGlobals.loadUtils) {
        ini(xe, setIti)
      } else {
        setTimeout(() => {
          ini(xe, setIti)
        }, 2500)
      }
    }, 2500)
  }
}

export default function SignIn() {
  const session = useSession()
  const router = useRouter()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [iti, setIti] = useState(null)

  useEffect(() => {
    try {
      bootPhone(setIti)
    } catch (err) {
      console.log(err)
      Sentry.captureException(err)
    }
  }, [])

  const onSubmit = async (data) => {
    setLoading(true)
    setError(false)

    try {
      const phone_number = iti.getNumber()
      const v = iti.isValidNumber()
      if (!v) {
        throw new Error('Invalid phone number provided')
      }

      await Auth.signUp({
        username: data.email.toLowerCase(),
        password: data.password,
        attributes: {
          email: data.email.toLowerCase(),
          given_name: data.firstName,
          family_name: data.lastName,
          phone_number,
        },
      })

      await timer(3000)

      const signInResult = await Auth.signIn({
        username: data.email.toLowerCase(),
        password: data.password,
      })

      const { idToken } = signInResult.signInUserSession
      const type = idToken && idToken.payload['cognito:groups'][0]
      const token = idToken && idToken.jwtToken
      const claims =
        idToken &&
          idToken.payload &&
          idToken.payload['https://hasura.io/jwt/claims']
          ? JSON.parse(idToken.payload['https://hasura.io/jwt/claims'])
          : {}

      session.setContext({
        loading: false,
        type,
        claims,
        token,
        user: signInResult as User,
      })

      recordSignup(signInResult)
      trackAction('sign_up')

      const contactNumber = claims && claims['x-user-contact-number']
        ? claims['x-user-contact-number']
        : '';

      try {
         // @ts-ignore
         window.FS && window.FS.identify(data.attributes.sub, {
          email: data.email,
          displayName: `${data.firstName} ${data.lastName}`,
          user_id: signInResult.claims['x-hasura-user-id']
        });

        fetch('/api/subscribe/', {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: data.email.toLowerCase()
          })
        })

        window.ttq && window.ttq.track('CompleteRegistration')
      } catch (err) {
        console.log(err)
      }

      try {
        // @ts-ignore
        klaviyo && klaviyo.push(['identify', {
          '$id': signInResult.attributes.sub,
          '$email': signInResult.attributes.email,
          '$first_name': signInResult.attributes.given_name,
          '$last_name': signInResult.attributes.family_name,
          '$phone_number': contactNumber
        }]);
      } catch (err) {
        console.log(err)
      }

      const redirect = localStorage.getItem('redirect') || '/account'
      setTimeout(() => router.push(redirect), 500)
    } catch (err) {
      console.log(err)
      setLoading(false)
      setError(err.message ? err.message : true)
      Sentry.captureException(err)
      Sentry.captureMessage(err.message)
      Sentry.captureMessage(data.email.toLowerCase())
    }
  }

  return (
    <Layout>
      <Head>
        <title>
          Sign Up | Soluna London | Elite Aesthetics, Skin, Laser & Beauty
        </title>
        <meta
          name="description"
          content="Create an account with Soluna London to avail of exclusive content, offers and to manage your appointments in London. Call 0333 016 3500"
        />
        <link rel="canonical" href={`https://solunalondon.com/sign-up/`} />
        <link
          rel="alternate"
          hrefLang="en-GB"
          href={`https://solunalondon.com/sign-up/`}
        />
        <link
          rel="alternate"
          hrefLang="en"
          href={`https://solunalondon.com/sign-up/`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`https://solunalondon.com/sign-up/`}
        />
      </Head>
      {loading && (
        <div className="fixed inset-0 z-50 flex justify-center items-center">
          <div className="relative z-50">
            <Loader width={80} height={80} stroke="#c3a46f" fill="#fff" />
          </div>
          <div className="fixed inset-0 bg-white opacity-60 z-40" />
        </div>
      )}
      <section className="flex flex-col container mx-auto px-6 items-center pt-12 pb-48">
        <h1 className="hidden">
          Create and account to take advantage of our services including laser
          hair removal, dermal fillers and advanced body treatments from our
          clinic based in Bromley
        </h1>
        <div className="flex flex-col w-full max-w-sm items-center mb-8">
          <div className="flex flex-col justify-center">
            <h2 className="mt-6 text-center text-3xl font-serif text-gray-800 font-medium">
              Create Account
            </h2>
            <Link
              href="/sign-in/"
              passHref
              className="text-brand-600 hover:text-brand-500 focus:outline-none focus:underline transition ease-in-out duration-150 text-center mt-4 font-light">
              
                Have an account? Sign in
              
            </Link>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="block mt-6 w-full">
            {error && (
              <p className="text-xs text-center font-medium mb-4 text-red-600">
                {typeof error === 'string'
                  ? error
                  : 'There was an error processing your request. Please check the information provided and try again'}
              </p>
            )}

            <div className="rounded-md shadow-sm">
              <div>
                <input
                  aria-label="Email address"
                  name="email"
                  type="email"
                  key="email"
                  id="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-4 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:shadow-outline-brand focus:z-10 sm:text-sm sm:leading-5 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400 font-light"
                  placeholder="Email address"
                  autoComplete="username"
                  {...register('email', { required: true })}
                />
              </div>
              <div className="-mt-px">
                <input
                  aria-label="Password"
                  name="password"
                  type="password"
                  key="password"
                  id="password"
                  required
                  className="appearance-none rounded-none relative block w-full px-4 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 focus:shadow-outline-brand focus:z-10 sm:text-sm sm:leading-5 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400 font-light"
                  placeholder="Password"
                  autoComplete="current-password"
                  {...register('password', { required: true })}
                />
              </div>
              <div className="-mt-px">
                <input
                  aria-label="Phone number"
                  name="phoneNumber"
                  type="tel"
                  key="phoneNumber"
                  id="phoneNumber"
                  required
                  className="appearance-none rounded-none relative block w-full px-4 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 focus:shadow-outline-brand focus:z-10 sm:text-sm sm:leading-5 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400 font-light"
                  placeholder="Phone number"
                  autoComplete="tel"
                  {...register('phoneNumber', { required: true })}
                />
              </div>
              <div className="-mt-px">
                <input
                  aria-label="First name"
                  name="firstName"
                  type="text"
                  key="firstName"
                  id="firstName"
                  required
                  className="appearance-none rounded-none relative block w-full px-4 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 focus:shadow-outline-brand focus:z-10 sm:text-sm sm:leading-5 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400 font-light"
                  placeholder="First name"
                  autoComplete="given-name"
                  {...register('firstName', { required: true })}
                />
              </div>
              <div className="-mt-px">
                <input
                  aria-label="Last name"
                  name="lastName"
                  type="text"
                  key="lastName"
                  id="lastName"
                  required
                  className="appearance-none rounded-none relative block w-full px-4 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:shadow-outline-brand  focus:z-10 sm:text-sm sm:leading-5 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400 font-light"
                  placeholder="Last name"
                  autoComplete="family-name"
                  {...register('lastName', { required: true })}
                />
              </div>
            </div>
            <div className="mt-6">
              <button type="submit" className="btn-primary">
                <span className="absolute left-0 inset-y pl-3">
                  <svg
                    className="h-5 w-5 text-brand-500 group-hover:text-white transition ease-in-out duration-150"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
                Create Account
              </button>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
}
